import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Paperclip, Play, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDevToolsStatus } from "@/hooks/useDevTools";
import { apiJson } from "@/lib/api-client";
import { cn } from "@/lib/utils";

type CaughtEmailSummary = {
  id: string;
  from_address: string;
  to_addresses: string[];
  subject: string;
  attachments: { filename: string; content_type: string; size_bytes: number }[];
  created_at: string;
};

type CaughtEmail = CaughtEmailSummary & { html: string; links: string[] };

type JobRunOutcome =
  | { status: "ok"; result: unknown }
  | { status: "skipped"; reason: string }
  | { status: "failed"; error: string };

function formatTimestamp(value: string) {
  return new Date(value).toLocaleString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function JobRunner() {
  const [jobName, setJobName] = useState("");
  const [now, setNow] = useState("");
  const [outcome, setOutcome] = useState<JobRunOutcome | null>(null);
  const queryClient = useQueryClient();

  const jobs = useQuery({
    queryKey: ["dev-jobs"],
    queryFn: () => apiJson<{ jobs: string[] }>("/api/dev/jobs"),
  });

  const runJob = useMutation({
    mutationFn: () =>
      apiJson<JobRunOutcome>(
        `/api/dev/jobs/${encodeURIComponent(jobName)}/run`,
        {
          method: "POST",
          body: JSON.stringify(now ? { now: new Date(now).toISOString() } : {}),
        },
      ),
    onSuccess: (data) => {
      setOutcome(data);
      void queryClient.invalidateQueries({ queryKey: ["caught-emails"] });
    },
    onError: (err: Error) => toast.error(`Job failed: ${err.message}`),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Run scheduled job</CardTitle>
        <CardDescription>
          Runs a job immediately. Set a time to run it as if it were that
          moment, e.g. a weekday at 07:05 for the task reminder digest.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="dev-job-name">Job</Label>
            <Select value={jobName} onValueChange={setJobName}>
              <SelectTrigger id="dev-job-name" data-testid="dev-job-select">
                <SelectValue placeholder="Select a job" />
              </SelectTrigger>
              <SelectContent>
                {(jobs.data?.jobs ?? []).map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dev-job-now">Run as of (optional)</Label>
            <Input
              id="dev-job-now"
              type="datetime-local"
              value={now}
              onChange={(event) => setNow(event.target.value)}
            />
          </div>
          <Button
            onClick={() => runJob.mutate()}
            disabled={!jobName || runJob.isPending}
            data-testid="dev-job-run"
          >
            <Play className="mr-2 h-4 w-4" />
            {runJob.isPending ? "Running..." : "Run"}
          </Button>
        </div>
        {outcome && (
          <pre
            className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs"
            data-testid="dev-job-outcome"
          >
            {JSON.stringify(outcome, null, 2)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

function EmailPreview({ id }: { id: string }) {
  const email = useQuery({
    queryKey: ["caught-email", id],
    queryFn: () => apiJson<{ email: CaughtEmail }>(`/api/dev/mail/${id}`),
  });

  if (!email.data) {
    return <p className="p-4 text-sm text-muted-foreground">Loading...</p>;
  }

  const { email: mail } = email.data;
  return (
    <div className="flex h-full flex-col" data-testid="caught-email-preview">
      <div className="space-y-1 border-b p-4 text-sm">
        <p className="text-base font-semibold">{mail.subject}</p>
        <p>
          <span className="text-muted-foreground">From:</span>{" "}
          {mail.from_address}
        </p>
        <p>
          <span className="text-muted-foreground">To:</span>{" "}
          {mail.to_addresses.join(", ")}
        </p>
        <p className="text-muted-foreground">
          {formatTimestamp(mail.created_at)}
        </p>
        {mail.attachments.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {mail.attachments.map((attachment) => (
              <Badge key={attachment.filename} variant="secondary">
                <Paperclip className="mr-1 h-3 w-3" />
                {attachment.filename} ({Math.ceil(attachment.size_bytes / 1024)}{" "}
                KB)
              </Badge>
            ))}
          </div>
        )}
      </div>
      <iframe
        title={mail.subject}
        srcDoc={`<base target="_blank">${mail.html}`}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        className="min-h-[480px] w-full flex-1 bg-white"
      />
    </div>
  );
}

export default function AdminMailCatcherPage() {
  const status = useDevToolsStatus();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");
  const [selectedID, setSelectedID] = useState<string | null>(null);

  const enabled = status.data?.mailCatcher === true;
  const emails = useQuery({
    queryKey: ["caught-emails", filter],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "200" });
      if (filter.includes("@")) params.set("to", filter);
      else if (filter) params.set("subject", filter);
      return apiJson<{ emails: CaughtEmailSummary[] }>(
        `/api/dev/mail?${params}`,
      );
    },
    enabled,
    refetchInterval: 5_000,
  });

  const clearAll = useMutation({
    mutationFn: () =>
      apiJson<{ deleted: number }>("/api/dev/mail", { method: "DELETE" }),
    onSuccess: (data) => {
      setSelectedID(null);
      toast.success(`Deleted ${data.deleted} emails`);
      void queryClient.invalidateQueries({ queryKey: ["caught-emails"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (status.isLoading) return null;

  if (!enabled) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeader title="Mail Catcher" />
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            The mail catcher is only available in dev-env and preview
            deployments. Emails in this environment are sent for real.
          </CardContent>
        </Card>
      </div>
    );
  }

  const list = emails.data?.emails ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Mail Catcher" />

      <JobRunner />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">Caught emails</CardTitle>
            <span className="text-sm text-muted-foreground">
              ({list.length})
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Input
                placeholder="Filter by recipient or subject"
                value={filter}
                onChange={(event) => setFilter(event.target.value.trim())}
                className="w-64"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => void emails.refetch()}
                aria-label="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => clearAll.mutate()}
                disabled={clearAll.isPending || list.length === 0}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Clear all
              </Button>
            </div>
          </div>
          <CardDescription>
            Nothing is sent from this environment; every outgoing email lands
            here instead.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid border-t md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div className="max-h-[640px] overflow-auto md:border-r">
              {list.length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">
                  No emails caught yet.
                </p>
              )}
              {list.map((email) => (
                <button
                  key={email.id}
                  type="button"
                  onClick={() => setSelectedID(email.id)}
                  data-testid="caught-email-row"
                  className={cn(
                    "block w-full border-b px-4 py-3 text-left text-sm hover:bg-muted/50",
                    selectedID === email.id && "bg-muted",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">
                      {email.subject}
                    </span>
                    {email.attachments.length > 0 && (
                      <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                    )}
                  </div>
                  <div className="truncate text-muted-foreground">
                    {email.to_addresses.join(", ")}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatTimestamp(email.created_at)}
                  </div>
                </button>
              ))}
            </div>
            <div className="min-h-[480px]">
              {selectedID ? (
                <EmailPreview id={selectedID} />
              ) : (
                <p className="p-4 text-sm text-muted-foreground">
                  Select an email to preview it.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
